import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Grid,
  Typography,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Snackbar,
  Alert
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import BasicLayout from "components/Layouts/BasicLayout";
import templatesService from "services/templates";
import { ITemplate } from "types/templates";
import { useAuth } from "context/Auth";
import noImage from "assets/images/no-image.jpg";

const TemplateGallery: React.FC = () => {
  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [addingTemplate, setAddingTemplate] = useState<Record<string, boolean>>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean, message: string, severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success"
  });
  const { currentOrganization } = useAuth();

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const response = await templatesService.fetchGallery();
        setTemplates(response.data);
      } catch (error) {
        console.error("Error fetching templates:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  const handleAddToOrganization = async (templateId: string) => {
    try {
      setAddingTemplate(prev => ({ ...prev, [templateId]: true }));

      // Find the template to add
      const templateToAdd = templates.find(t => (t.id || t._id) === templateId);

      if (!templateToAdd) {
        throw new Error("Template not found");
      }

      // Copy the template and set the organization property
      const newTemplate = {
        ...templateToAdd,
        organization: currentOrganization?.id || currentOrganization?._id,
        shareable: false
      };
      delete newTemplate.id;
      delete newTemplate._id;
      // Create a new template based on the gallery template
      await templatesService.create(newTemplate);

      setSnackbar({
        open: true,
        message: "Template added to your organization successfully",
        severity: "success"
      });
    } catch (error) {
      console.error("Error adding template to organization:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || error?.response?.data?.error || "Failed to add template to your organization",
        severity: "error"
      });
    } finally {
      setAddingTemplate(prev => ({ ...prev, [templateId]: false }));
    }
  };

  const isTemplateAlreadyAdded = (templateSlug: string) => {
    // Check if the current organization has a template with the same slug
    return currentOrganization?.templates?.[templateSlug];
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <BasicLayout>
      <Grid container spacing={4}>
        <Grid item xs={6} display="flex" alignItems="center">
          <Typography variant="h4">Template Gallery</Typography>
        </Grid>
        <Grid item xs={6} display="flex" justifyContent="flex-end" alignItems="flex-end" gap={2}>
          <Button variant="contained" color="primary" component={Link} to="/templates">
            Go to My Templates
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body1" paragraph>
            Browse and add pre-designed templates to your organization.
          </Typography>
        </Grid>

        {loading ? (
          <Grid item xs={12} display="flex" justifyContent="center">
            <CircularProgress />
          </Grid>
        ) : templates.length === 0 ? (
          <Grid item xs={12}>
            <Typography variant="body1">No templates available in the gallery.</Typography>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {templates.map((template) => (
                <Grid item xs={12} sm={6} md={4} key={template.id || template._id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardMedia
                      component="img"
                      height="180"
                      image={template.defaultImage?.url || noImage}
                      alt={template.name}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography gutterBottom variant="h5" component="div">
                        {template.name}
                      </Typography>
                      <Chip
                        label={template.type}
                        size="small"
                        color={
                          template.type === 'email' ? 'primary' :
                            template.type === 'sms' ? 'secondary' : 'default'
                        }
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {template.description || 'No description available'}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddToOrganization(template.id || template._id || '')}
                        disabled={isTemplateAlreadyAdded(template.slug || '') || addingTemplate[template.id || template._id || '']}
                      >
                        {addingTemplate[template.id || template._id || ''] ? (
                          <CircularProgress size={20} />
                        ) : isTemplateAlreadyAdded(template.slug || '') ? (
                          'Already Added'
                        ) : (
                          'Add to My Templates'
                        )}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}
      </Grid>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </BasicLayout>
  );
};

export default TemplateGallery; 